import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import {
  DndContext,
  DragOverlay,
  DragEndEvent,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Fragment, ReactNode, useState } from 'react'
import { FiPlus, FiShoppingBag, FiTag, FiTruck, FiX } from 'react-icons/fi'

import { IValue } from '@base/@types/components/Shared/Filter'
import { IFilter } from '@base/@types/providers/Shared/FiltersProvider'
import Filter from '@base/components/Shared/Filter'

import { ScheduleType } from './CalendarPurchase'
import { ScheduleItem } from './types'

interface ScheduleGridProps {
  data: ScheduleItem[]
  type: ScheduleType
  onAdd?: (
    day: number,
    fornecedorId: number,
    lojaId: number,
    categoriaCompactaId: number
  ) => void
  onMergeItems?: (keepItemId: number, removeItemId: number) => void
  onDelete?: (id: number) => void
  lojas?: IFilter[]
  fornecedores?: { value: number; label: string }[]
  categorias?: { value: number; label: string }[]
  onFornecedorChange: (fornecedorId: number) => void
  onDragEnd: (id: number, day: number) => void
  enableHoverEffect?: boolean
}

const days = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta']

const AddPopover = ({
  group,
  dayIndex,
  lojas,
  fornecedores,
  categorias,
  onFornecedorChange,
  onAdd,
  checkAndMergeItems,
  formValues,
  setFormValues,
  clearFormValues
}: {
  group: number | string
  dayIndex: number
  lojas: IFilter[]
  fornecedores: { value: number; label: string }[]
  categorias: { value: number; label: string }[]
  onFornecedorChange: (fornecedorId: number) => void
  onAdd: (
    day: number,
    fornecedorId: number,
    lojaId: number,
    categoriaId: number
  ) => void
  checkAndMergeItems?: (
    targetDay: number,
    fornecedorId: number,
    lojaId: number,
    categoriaCompactaId: number
  ) => boolean
  formValues: Record<
    string,
    { lojaId?: number; fornecedorId?: number; categoriaCompactaIds?: number[] }
  >
  setFormValues: any
  clearFormValues: (key: string) => void
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const popoverKey = `${group}-${dayIndex}`
  const currentFormValues = formValues[popoverKey] || {}

  const handleAdd = () => {
    const values = formValues[popoverKey]

    if (
      values?.lojaId &&
      values?.fornecedorId &&
      values.categoriaCompactaIds &&
      values.categoriaCompactaIds.length > 0
    ) {
      // Create separate items for each selected category
      values.categoriaCompactaIds.forEach(categoriaId => {
        // Verificar duplicata antes de adicionar
        const wasMerged = checkAndMergeItems?.(
          dayIndex,
          values.fornecedorId!,
          values.lojaId!,
          categoriaId
        )

        if (!wasMerged) {
          // Só adicionar se não houve merge
          onAdd(dayIndex, values.fornecedorId!, values.lojaId!, categoriaId)
        }
      })
      // Clear form values after successful add
      clearFormValues(popoverKey)
      // Close popover
      onClose()
    }
  }

  const handleOpen = () => {
    clearFormValues(popoverKey)
    onOpen()
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={onClose}
      placement="left"
    >
      <PopoverTrigger>
        <IconButton
          icon={<FiPlus size={12} />}
          aria-label="Adicionar"
          size="xs"
          isRound
          variant="solid"
          colorScheme="blue"
          width="24px"
          height="24px"
          minW="unset"
          p={0}
          mx="auto"
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent bg="white" boxShadow="lg">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontWeight="bold" bg="white">
            Nova Programação
          </PopoverHeader>
          <PopoverBody bg="white">
            <Flex direction="column" gap={3}>
              <Filter
                label="Selecionar loja"
                options={lojas}
                value={
                  currentFormValues.lojaId
                    ? ([
                        lojas.find(
                          loja => loja.value === currentFormValues.lojaId
                        )
                      ].filter(Boolean) as IValue[])
                    : []
                }
                isMulti={false}
                onChange={(selectedOption: any) => {
                  const selectedValue = selectedOption?.value
                  setFormValues((prev: any) => ({
                    ...prev,
                    [popoverKey]: {
                      ...prev[popoverKey],
                      lojaId: selectedValue || undefined
                    }
                  }))
                }}
              />
              <Filter
                label="Selecionar fornecedor"
                options={fornecedores}
                value={
                  currentFormValues.fornecedorId
                    ? ([
                        fornecedores.find(
                          f => f.value === currentFormValues.fornecedorId
                        )
                      ].filter(Boolean) as IValue[])
                    : []
                }
                isMulti={false}
                onChange={(selectedOption: any) => {
                  const selectedValue = selectedOption?.value
                  if (selectedValue) {
                    onFornecedorChange(selectedValue)
                  }
                  setFormValues((prev: any) => ({
                    ...prev,
                    [popoverKey]: {
                      ...prev[popoverKey],
                      fornecedorId: selectedValue || undefined,
                      categoriaCompactaIds: []
                    }
                  }))
                }}
              />
              <Filter
                label="Selecionar categorias"
                options={categorias}
                value={
                  currentFormValues.categoriaCompactaIds
                    ? categorias.filter(c =>
                        currentFormValues.categoriaCompactaIds?.includes(
                          c.value
                        )
                      )
                    : []
                }
                isMulti={true}
                isDisabled={!currentFormValues.fornecedorId}
                onChange={(selectedOptions: any) => {
                  const selectedIds = selectedOptions
                    ? selectedOptions.map((option: any) => option.value)
                    : []
                  setFormValues((prev: any) => ({
                    ...prev,
                    [popoverKey]: {
                      ...prev[popoverKey],
                      categoriaCompactaIds: selectedIds
                    }
                  }))
                }}
              />
              <Button
                colorScheme="blue"
                size="sm"
                onClick={handleAdd}
                isDisabled={
                  !currentFormValues.lojaId ||
                  !currentFormValues.fornecedorId ||
                  !currentFormValues.categoriaCompactaIds ||
                  currentFormValues.categoriaCompactaIds.length === 0
                }
                width="full"
              >
                Adicionar
              </Button>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

const DraggableScheduleCard = ({
  match,
  type,
  onDelete,
  enableHoverEffect = true
}: {
  match: ScheduleItem
  type: ScheduleType
  onDelete: (id: number) => void
  enableHoverEffect?: boolean
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: match.id
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      key={match.id}
      bg="gray.100"
      borderLeft="4px solid"
      borderColor="green.500"
      borderRadius="md"
      boxShadow="sm"
      p={3}
      pr={8}
      m={1}
      position="relative"
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      transform="scale(1)"
      zIndex={isDragging ? 5 : 1}
      opacity={isDragging ? 0.7 : 1}
      _hover={
        !isDragging && enableHoverEffect
          ? {
              transform: 'scale(1.2) translateY(-12px)',
              boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
              zIndex: 10,
              bg: 'white',
              borderColor: 'blue.500',
              borderWidth: '2px',
              borderLeftWidth: '4px',
              minHeight: 'auto',
              height: 'auto',
              width: 'auto',
              minWidth: 'max-content',
              maxWidth: 'none',
              padding: '16px',
              '& .delete-btn': {
                opacity: 1
              },
              '& .card-text': {
                fontWeight: 'bold',
                fontSize: 'md',
                overflow: 'visible !important',
                whiteSpace: 'nowrap !important',
                textOverflow: 'unset !important',
                display: 'inline-block !important',
                WebkitLineClamp: 'unset !important',
                WebkitBoxOrient: 'unset !important',
                lineHeight: '1.3',
                width: 'auto',
                minWidth: 'max-content'
              },
              '& .card-subtitle': {
                color: 'blue.600',
                fontWeight: 'semibold',
                fontSize: 'sm',
                overflow: 'visible !important',
                whiteSpace: 'nowrap !important',
                textOverflow: 'unset !important',
                display: 'inline-block !important',
                WebkitLineClamp: 'unset !important',
                WebkitBoxOrient: 'unset !important',
                lineHeight: '1.4',
                width: 'auto',
                minWidth: 'max-content'
              },
              '& .card-icon': {
                color: 'blue.500',
                transform: 'scale(1.1)'
              },
              '& .chakra-stack': {
                gap: '8px'
              }
            }
          : !isDragging && !enableHoverEffect
          ? {
              transform: 'translateY(-2px)',
              boxShadow: 'md',
              '& .delete-btn': {
                opacity: 1
              }
            }
          : {}
      }
    >
      <Box {...listeners} cursor="grab" _active={{ cursor: 'grabbing' }}>
        <VStack align="start" spacing={1}>
          {type === 'LOJA' && (
            <>
              <HStack spacing={2}>
                <Icon
                  as={FiTruck}
                  className="card-icon"
                  color="gray.600"
                  transition="all 0.2s"
                />
                <Text
                  className="card-text"
                  fontWeight="bold"
                  fontSize="sm"
                  noOfLines={2}
                  transition="all 0.2s"
                >
                  {enableHoverEffect
                    ? `${match.fornecedorId} - ${match.fornecedorNome}`
                    : match.fornecedorNome}
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Icon
                  as={FiTag}
                  className="card-icon"
                  color="gray.500"
                  transition="all 0.2s"
                />
                <Text
                  className="card-subtitle"
                  fontSize="xs"
                  color="gray.600"
                  noOfLines={1}
                  transition="all 0.2s"
                >
                  {enableHoverEffect
                    ? `${match.categoriaCompactaId} - ${match.categoriaCompactaNome}`
                    : match.categoriaCompactaNome}
                </Text>
              </HStack>
            </>
          )}
          {type === 'FORNECEDOR' && (
            <>
              <HStack spacing={2}>
                <Icon
                  as={FiShoppingBag}
                  className="card-icon"
                  color="gray.600"
                  transition="all 0.2s"
                />
                <Text
                  className="card-text"
                  fontWeight="bold"
                  fontSize="sm"
                  noOfLines={2}
                  transition="all 0.2s"
                >
                  {enableHoverEffect
                    ? `${match.lojaId} - ${match.lojaNome}`
                    : match.lojaNome}
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Icon
                  as={FiTag}
                  className="card-icon"
                  color="gray.500"
                  transition="all 0.2s"
                />
                <Text
                  className="card-subtitle"
                  fontSize="xs"
                  color="gray.600"
                  noOfLines={1}
                  transition="all 0.2s"
                >
                  {enableHoverEffect
                    ? `${match.categoriaCompactaId} - ${match.categoriaCompactaNome}`
                    : match.categoriaCompactaNome}
                </Text>
              </HStack>
            </>
          )}
          {type === 'ALL' && (
            <>
              <HStack spacing={2}>
                <Icon
                  as={FiTruck}
                  className="card-icon"
                  color="gray.600"
                  transition="all 0.2s"
                />
                <Text
                  className="card-subtitle"
                  fontSize="xs"
                  color="gray.700"
                  noOfLines={1}
                  transition="all 0.2s"
                >
                  {enableHoverEffect
                    ? `${match.fornecedorId} - ${match.fornecedorNome}`
                    : match.fornecedorNome}
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Icon
                  as={FiShoppingBag}
                  className="card-icon"
                  color="gray.600"
                  transition="all 0.2s"
                />
                <Text
                  className="card-subtitle"
                  fontSize="xs"
                  color="gray.700"
                  noOfLines={1}
                  transition="all 0.2s"
                >
                  {enableHoverEffect
                    ? `${match.lojaId} - ${match.lojaNome}`
                    : match.lojaNome}
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Icon
                  as={FiTag}
                  className="card-icon"
                  color="gray.500"
                  transition="all 0.2s"
                />
                <Text
                  className="card-subtitle"
                  fontSize="xs"
                  color="gray.700"
                  noOfLines={1}
                  transition="all 0.2s"
                >
                  {enableHoverEffect
                    ? `${match.categoriaCompactaId} - ${match.categoriaCompactaNome}`
                    : match.categoriaCompactaNome}
                </Text>
              </HStack>
            </>
          )}
          {type === 'CATEGORIA_COMPACTA' && (
            <>
              <HStack spacing={2}>
                <Icon
                  as={FiShoppingBag}
                  className="card-icon"
                  color="gray.600"
                  transition="all 0.2s"
                />
                <Text
                  className="card-text"
                  fontWeight="bold"
                  fontSize="sm"
                  noOfLines={2}
                  transition="all 0.2s"
                >
                  {enableHoverEffect
                    ? `${match.lojaId} - ${match.lojaNome}`
                    : match.lojaNome}
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Icon
                  as={FiTruck}
                  className="card-icon"
                  color="gray.600"
                  transition="all 0.2s"
                />
                <Text
                  className="card-subtitle"
                  fontSize="xs"
                  color="gray.700"
                  noOfLines={1}
                  transition="all 0.2s"
                >
                  {enableHoverEffect
                    ? `${match.fornecedorId} - ${match.fornecedorNome}`
                    : match.fornecedorNome}
                </Text>
              </HStack>
            </>
          )}
        </VStack>
      </Box>
      <IconButton
        icon={<FiX />}
        aria-label="Remover"
        size="xs"
        variant="ghost"
        colorScheme="red"
        position="absolute"
        top="4px"
        right="4px"
        className="delete-btn"
        opacity={0}
        onClick={e => {
          e.stopPropagation()
          onDelete?.(match.id)
        }}
      />
    </Box>
  )
}

const DROP_PREFIX = 'day-'

const buildDropId = (
  type: ScheduleType,
  dayIndex: number,
  group?: number | string
) =>
  type === 'ALL'
    ? `${DROP_PREFIX}${dayIndex}`
    : `${DROP_PREFIX}${group}-${dayIndex}`

// "day-3" → { day: 3 } | "day-1000-3" → { group: 1000, day: 3 }; null se não for coluna de dia
const parseDropId = (
  overId: string | number
): { day: number; group?: number | string } | null => {
  if (typeof overId !== 'string' || !overId.startsWith(DROP_PREFIX)) return null
  const rest = overId.slice(DROP_PREFIX.length)
  const sep = rest.lastIndexOf('-')
  if (sep <= 0) return { day: Number(rest) }
  const groupRaw = rest.slice(0, sep)
  return {
    day: Number(rest.slice(sep + 1)),
    group: isNaN(Number(groupRaw)) ? groupRaw : Number(groupRaw)
  }
}

const DroppableDayColumn = ({
  dayIndex,
  children,
  isOver,
  group,
  type
}: {
  dayIndex: number
  children: ReactNode
  isOver?: boolean
  group?: number | string
  type: ScheduleType
}) => {
  // ID único por grupo+dia, com prefixo para não colidir com os ids numéricos dos cards
  // (cards novos têm id negativo, que também não pode ser confundido com "grupo-dia")
  const dropId = buildDropId(type, dayIndex, group)

  const { setNodeRef, isOver: isOverFromDroppable } = useDroppable({
    id: dropId
  })

  const finalIsOver = isOver || isOverFromDroppable

  return (
    <Box
      ref={setNodeRef}
      borderTop="1px solid"
      borderColor={finalIsOver ? 'blue.300' : 'gray.200'}
      bg={finalIsOver ? 'blue.50' : 'transparent'}
      p={2}
      m={2}
      minH="100px"
      transition="all 0.2s ease-in-out"
      _hover={
        !finalIsOver
          ? {
              borderColor: 'gray.300',
              bg: 'gray.25'
            }
          : {}
      }
    >
      {children}
    </Box>
  )
}

export const ScheduleGrid = ({
  data,
  type,
  onAdd,
  onMergeItems,
  onDelete,
  lojas = [],
  fornecedores = [],
  categorias = [],
  onFornecedorChange,
  onDragEnd,
  enableHoverEffect = true
}: ScheduleGridProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8 // 8px de movimento antes de ativar o drag
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const [activeId, setActiveId] = useState<number | null>(null)

  // Função para verificar e unificar items duplicados
  const checkAndMergeItems = (
    targetDay: number,
    fornecedorId: number,
    lojaId: number,
    categoriaCompactaId: number,
    draggedItemId?: number
  ) => {
    // Encontrar item existente no mesmo dia com mesmos atributos
    const existingItem = data.find(
      item =>
        item.diaDaSemana === targetDay &&
        item.fornecedorId === fornecedorId &&
        item.lojaId === lojaId &&
        item.categoriaCompactaId === categoriaCompactaId &&
        item.id !== draggedItemId // Excluir o próprio item sendo arrastado
    )

    if (existingItem && draggedItemId && onMergeItems) {
      onMergeItems(existingItem.id, draggedItemId)
      return true // Indica que houve merge
    }

    return false // Nenhum merge necessário
  }

  const handleDragStart = (event: DragEndEvent) => {
    setActiveId(Number(event.active.id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (!over) {
      // Se não há target de drop, o item volta à posição original (não faz nada)
      return
    }

    const itemId = Number(active.id)
    const overId = over.id

    // Encontrar o item que está sendo arrastado
    const draggedItem = data.find(item => item.id === itemId)
    if (!draggedItem) {
      return
    }

    // Verificar se foi solto sobre uma drop zone válida
    const validDays = [1, 2, 3, 4, 5]
    let targetDay: number | null = null
    const groupKey: keyof ScheduleItem | null =
      type === 'ALL'
        ? null
        : type === 'FORNECEDOR'
        ? 'fornecedorId'
        : type === 'LOJA'
        ? 'lojaId'
        : 'categoriaCompactaId'

    const dropTarget = parseDropId(overId)
    if (dropTarget) {
      // Solto numa coluna de dia
      targetDay = dropTarget.day
      // Views agrupadas: não move item entre grupos (linhas) diferentes
      if (
        groupKey &&
        dropTarget.group !== undefined &&
        draggedItem[groupKey] !== dropTarget.group
      ) {
        return
      }
    } else {
      // Solto sobre outro card
      const targetItem = data.find(item => item.id === Number(overId))
      if (targetItem) {
        targetDay = targetItem.diaDaSemana

        // Só permite o drop se for o mesmo grupo (mesma linha)
        if (groupKey && draggedItem[groupKey] !== targetItem[groupKey]) {
          return
        }
      }
    }

    if (targetDay && validDays.includes(targetDay)) {
      // Só verificar merge se o item está sendo movido para um dia diferente
      if (draggedItem.diaDaSemana !== targetDay) {
        // Verificar se há merge necessário antes de fazer o drop
        const wasMerged = checkAndMergeItems(
          targetDay,
          draggedItem.fornecedorId,
          draggedItem.lojaId,
          draggedItem.categoriaCompactaId,
          itemId
        )

        if (!wasMerged) {
          // Só fazer o drop se não houve merge
          onDragEnd(itemId, targetDay)
        }
      } else {
        // Se está no mesmo dia, apenas fazer o reposicionamento
        onDragEnd(itemId, targetDay)
      }
    }
  }

  const handleDragCancel = () => {
    setActiveId(null)
  }
  const [formValues, setFormValues] = useState<
    Record<
      string,
      {
        lojaId?: number
        fornecedorId?: number
        categoriaCompactaIds?: number[] // Changed to array for multi-select
      }
    >
  >({})

  const clearFormValues = (key: string) => {
    setFormValues(prev => ({
      ...prev,
      [key]: {
        lojaId: undefined,
        fornecedorId: undefined,
        categoriaCompactaIds: []
      }
    }))
  }
  const groupKey: keyof ScheduleItem =
    type === 'FORNECEDOR'
      ? 'fornecedorId'
      : type === 'LOJA'
      ? 'lojaId'
      : 'categoriaCompactaId'
  const groupNameKey: keyof ScheduleItem =
    type === 'FORNECEDOR'
      ? 'fornecedorNome'
      : type === 'LOJA'
      ? 'lojaNome'
      : 'categoriaCompactaNome'

  const groups =
    type === 'ALL' ? ['ALL'] : Array.from(new Set(data.map(d => d[groupKey])))
  const groupNames = new Map(
    data?.map(item => [item[groupKey], item[groupNameKey]])
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <Box overflowX="auto">
        <Grid
          templateColumns={`${type !== 'ALL' ? '250px ' : ''}repeat(5, 1fr)`}
          border="1px solid"
          borderColor="gray.200"
          fontSize="sm"
        >
          {type !== 'ALL' && (
            <Box bg="green.700" textAlign="center" p={2}>
              <Text fontWeight="bold" color="white">
                {type === 'FORNECEDOR'
                  ? 'Fornecedor'
                  : type === 'LOJA'
                  ? 'Loja'
                  : 'Categoria Compacta'}
              </Text>
            </Box>
          )}
          {days.map(d => (
            <Box key={d} bg="green.700" textAlign="center" p={2}>
              <Text color="white" fontWeight="bold">
                {d}
              </Text>
            </Box>
          ))}

          {groups.map(group => (
            <Fragment key={String(group)}>
              {type !== 'ALL' && (
                <Box
                  p={2}
                  bg="gray.50"
                  borderTop="1px solid"
                  borderColor="gray.200"
                  fontWeight="medium"
                >
                  <Text>{groupNames.get(group as any) || `ID: ${group}`}</Text>
                </Box>
              )}

              {days.map((_, i) => {
                const dayIndex = i + 1

                const itemsInDay = data.filter(
                  d =>
                    d.diaDaSemana === dayIndex &&
                    (type === 'ALL' || d[groupKey] === group)
                )

                return (
                  <DroppableDayColumn
                    key={`${group}-${dayIndex}`}
                    dayIndex={dayIndex}
                    group={group}
                    type={type}
                  >
                    <SortableContext
                      items={itemsInDay.map(item => item.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Flex direction="column" gap={2} alignItems="center">
                        {itemsInDay.map(match => (
                          <DraggableScheduleCard
                            key={match.id}
                            match={match}
                            type={type}
                            onDelete={onDelete}
                            enableHoverEffect={enableHoverEffect}
                          />
                        ))}

                        <AddPopover
                          group={group}
                          dayIndex={dayIndex}
                          lojas={lojas}
                          fornecedores={fornecedores}
                          categorias={categorias}
                          onFornecedorChange={onFornecedorChange}
                          onAdd={onAdd}
                          checkAndMergeItems={checkAndMergeItems}
                          formValues={formValues}
                          setFormValues={setFormValues}
                          clearFormValues={clearFormValues}
                        />
                      </Flex>
                    </SortableContext>
                  </DroppableDayColumn>
                )
              })}
            </Fragment>
          ))}
        </Grid>
      </Box>
      <DragOverlay>
        {activeId ? (
          <DraggableScheduleCard
            match={data.find(item => item.id === activeId)}
            type={type}
            onDelete={onDelete}
            enableHoverEffect={enableHoverEffect}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
